/**
 * nutri-leaf-cart.js — single Ajax cart contract for every purchase surface
 * (P0-10). No section-specific JS adds to cart directly anymore; they all
 * call NLCart.add().
 *
 * Contract:
 *   1. POST to window.routes.cart_add_url (locale-aware) with the section
 *      list that matches the cart component actually rendered by the layout:
 *      `cart_type: drawer` → <cart-drawer> → sections ['cart-drawer'],
 *      any other value → <cart-notification> → notification sections.
 *   2. Hand the returned markup to that component's Dawn renderContents() so
 *      items, totals, and live regions all update from one render.
 *   3. Dispatch a single `cart:refresh` DOM event; the header badge listens
 *      for it (cart.js also dispatches it after drawer/cart-page edits).
 *
 * If no component is present (cart page), the add still succeeds and only
 * the count event fires.
 */
(function () {
  'use strict';

  function activeComponent() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      return { el: drawer, sections: ['cart-drawer'] };
    }
    const notification = document.querySelector('cart-notification');
    if (notification) {
      return {
        el: notification,
        sections: ['cart-notification-product', 'cart-notification-button'],
      };
    }
    return { el: null, sections: [] };
  }

  window.NLCart = {
    async add(variantId, quantity, sellingPlanId) {
      const component = activeComponent();
      const body = {
        id: variantId,
        quantity: quantity,
        sections_url: window.location.pathname,
      };
      // Subscription purchase (P1-53): without selling_plan the cart line
      // falls back to a one-time purchase at the regular price.
      if (sellingPlanId) body.selling_plan = sellingPlanId;
      if (component.sections.length) body.sections = component.sections;

      const res = await fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
        method: 'POST',
        // Accept: application/javascript marks this as an AJAX cart request —
        // without it Shopify answers the HTML form POST with a 302 redirect to
        // /cart and the JSON parse below fails, breaking add-to-cart entirely.
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(function () { return null; });
      if (!res.ok || !data) {
        throw new Error((data && data.description) || 'Cart request failed (' + res.status + ')');
      }
      if (component.el && typeof component.el.renderContents === 'function') {
        component.el.renderContents(data);
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return data;
    },
  };

  // Product-card quick-add (RV-01): the plain POST would full-page-redirect
  // and skip the drawer / cart:refresh contract. Intercept and route every
  // .nl-product-card__add-form through NLCart.add().
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('.nl-product-card__add-form');
    if (!form) return;
    e.preventDefault();
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
    const errSlot = form.querySelector('.nl-product-card__error');
    if (errSlot) errSlot.hidden = true;
    window.NLCart.add(parseInt(idInput.value, 10), 1, null)
      .catch(function () {
        // P0.5 (2026-08-17): no silent commerce failures — surface a concise
        // customer-facing message in the card (aria-live status announces it)
        // and keep the button retryable. Text is a locale key injected via
        // data-error-text by nl-product-card.liquid.
        if (errSlot) {
          errSlot.textContent = errSlot.dataset.errorText || 'Sorry, adding to cart failed. Please try again.';
          errSlot.hidden = false;
        }
      })
      .then(function () { if (btn) btn.disabled = false; });
  });
})();
